import { TEmployeeRenewVisaDocumentStepGroup } from "@/api/entities/employee-renew-visa-document-step-group.entity";
import { uploadFileApi } from "@/api/services/global/file";
import { useGetResidenceStatusList } from "@/api/services/global/master";
import { createOrUpdateRenewVisaResidenceCardInformationGroupListApi } from "@/api/services/main/renew-visa";
import { TResidenceCardInformationReqDto } from "@/api/services/main/renew-visa/dtos/create-or-update-renew-visa-residence-card-information-group-list.dto";
import { TGetRenewVisaResidenceCardInformationGroupOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-residence-card-information-group.dto";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT, DATE_NOT_TIMEZONE_FORMAT, DATE_TIME_FORMAT } from "@/constants/datetime";
import { FILE_STATUS, FILE_UPLOAD_FOR } from "@/constants/file";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { RenewVisaStepCard } from "@/features/renew-visa/components/RenewVisaStepCard";
import { UploadFileBtn } from "@/features/renew-visa/components/residenceCardInfoStep/UploadFileBtn";
import { WarningExcessResidenceCardFileModal } from "@/features/renew-visa/components/residenceCardInfoStep/WarningExcessResidenceCardFileModal";
import { useCheckRenewVisaHistoryPage } from "@/features/renew-visa/hook/useCheckRenewVisaHistoryPage";
import { renewVisaResidenceCardInformationSchema } from "@/features/renew-visa/schema/renewVisaResidenceCardInfoSchema";
import { TRenewVisaResidenceCardInformationForm } from "@/features/renew-visa/types/renewVisaResidenceCardInfoForm";
import { useTable } from "@/hooks/useTable";
import { divider } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { TExtendFile } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { getFileNameFromPath } from "@/utils/file";
import { toNumber } from "@/utils/number";
import { findLabelByValue } from "@/utils/object";
import { makeUuid } from "@/utils/pieces";
import { getFullName, truncateWithEllipsis } from "@/utils/string";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export type TResidenceCardInfoStepCardProps = {
  stepGroupData: TEmployeeRenewVisaDocumentStepGroup;
  residenceCardInformationGroupListData?: TGetRenewVisaResidenceCardInformationGroupOutputDto[];
  readonly: boolean;
  isExistBasicInfoData: boolean;
};

export const ResidenceCardInfoStepCard: FC<TResidenceCardInfoStepCardProps> = ({
  stepGroupData,
  residenceCardInformationGroupListData,
  isExistBasicInfoData,
  readonly,
}) => {
  const { stepOrder, stepName } = stepGroupData.masterDocumentStep;
  const { id: documentStepGroupId, status } = stepGroupData;

  const { employeeRenewVisaDocumentGroupId } = useParams();
  const queryClient = useQueryClient();

  const methods = useForm<TRenewVisaResidenceCardInformationForm>({
    resolver: yupResolver(renewVisaResidenceCardInformationSchema),
    defaultValues: {
      residenceCardInformation: residenceCardInformationGroupListData?.map((item) => ({
        employeeId: toNumber(item.employee.id),
        renewVisaDocumentStepId: toNumber(item.renewVisaDocumentStep.id),
        renewVisaDocumentId: toNumber(item.renewVisaDocumentId),
        residenceExpiryDate: item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceExpiryDate
          ? dayjs(item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceExpiryDate)
          : undefined,
        residenceCode: item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceCode ?? undefined,
        residencePeriodId: item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residencePeriodId?.toString() ?? undefined,
        residenceStatusId:
          item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceStatusId?.toString() ??
          item.employee.residenceStatusId?.toString() ??
          undefined,
        residenceCardBackFile: item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceCardBackFilePath
          ? item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceCardBackFilePath
              .filter((item) => !!item)
              .map(
                (filePath) =>
                  ({
                    id: makeUuid(),
                    status: FILE_STATUS.OK,
                    name: filePath ? getFileNameFromPath(filePath) : null,
                    filePath: filePath,
                  }) as TExtendFile,
              )
          : [],
        residenceCardFrontFile: item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceCardFrontFilePath
          ? item.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceCardFrontFilePath
              .filter((item) => !!item)
              .map(
                (filePath) =>
                  ({
                    id: makeUuid(),
                    status: FILE_STATUS.OK,
                    name: filePath ? getFileNameFromPath(filePath) : null,
                    filePath: filePath,
                  }) as TExtendFile,
              )
          : [],
      })),
    },
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control } = methods;

  const { data: residenceStatus, isLoading: isLoadingResidenceStatus, residenceStatusOptions } = useGetResidenceStatusList();

  const getResidencePeriodOptions = (residenceStatusId: string) => {
    const founded = residenceStatus ? residenceStatus.find((item) => toNumber(item.id) === toNumber(residenceStatusId)) : undefined;
    if (founded) return founded.residencePeriods.map((item) => ({ label: item.residencePeriod ?? "", value: item.id, hidden: item.isHidden }));
    else return [];
  };

  const onSubmitRenewVisaResidenceCardInformation = async (data: TRenewVisaResidenceCardInformationForm) => {
    if (!employeeRenewVisaDocumentGroupId || !documentStepGroupId) return;
    try {
      const residenceCardInformationPayload: TResidenceCardInformationReqDto[] = await Promise.all(
        data.residenceCardInformation.map(async (item) => {
          const residenceCardBackFilePath =
            item.residenceCardBackFile && item.residenceCardBackFile?.length > 0
              ? (
                  await Promise.all(
                    item.residenceCardBackFile.map(async (file) => {
                      if (file?.size) {
                        const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_RESIDENCE_CARD_BACK_FILE });
                        return rs?.data.filePath;
                      } else {
                        return file?.filePath || "";
                      }
                    }),
                  )
                ).filter((item) => item)
              : [];
          const residenceCardFrontFilePath =
            item.residenceCardFrontFile && item.residenceCardFrontFile?.length > 0
              ? (
                  await Promise.all(
                    item.residenceCardFrontFile.map(async (file) => {
                      if (file?.size) {
                        const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.EMPLOYEE_RESIDENCE_CARD_FRONT_FILE });
                        return rs?.data.filePath;
                      } else {
                        return file?.filePath || "";
                      }
                    }),
                  )
                ).filter((item) => item)
              : [];
          return {
            employeeId: item.employeeId,
            renewVisaDocumentStepId: item.renewVisaDocumentStepId,
            renewVisaDocumentId: item.renewVisaDocumentId,
            residenceCode: item.residenceCode,
            residencePeriodId: toNumber(item.residencePeriodId),
            residenceStatusId: toNumber(item.residenceStatusId),
            residenceExpiryDate: item.residenceExpiryDate.format(DATE_NOT_TIMEZONE_FORMAT),
            residenceCardBackFilePath,
            residenceCardFrontFilePath,
          };
        }),
      );

      const rs = await createOrUpdateRenewVisaResidenceCardInformationGroupListApi(employeeRenewVisaDocumentGroupId, documentStepGroupId, {
        residenceCardInformation: residenceCardInformationPayload,
      });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    }
  };

  const { isHistoryPage } = useCheckRenewVisaHistoryPage();

  const columns = useMemo<MRT_ColumnDef<TGetRenewVisaResidenceCardInformationGroupOutputDto>[]>(
    () => [
      {
        header: "外国人名",
        Cell: ({ row }) => {
          const fullName = getFullName({
            firstName: row.original.employee.firstName,
            lastName: row.original.employee.lastName,
            middleName: row.original.employee.middleName,
          });
          return (
            <Box display="flex" alignItems="center" gap={1.75} mt={-0.5}>
              <Avatar filePath={row.original.employee.thumbnailForListAvatarFilePath} alt="avatar" width={30} height={40} />
              <Typography variant="body14" padding="0px !important">
                {fullName ? truncateWithEllipsis(fullName, 26) : EMPTY_STR.TEXT}
              </Typography>
            </Box>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset", backgroundColor: divider.low },
        },
      },
      {
        header: "在留カード番号",
        Cell: ({ row }) => {
          if (isHistoryPage)
            return (
              <Typography variant="body14" padding="0px !important">
                {row.original.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceCode}
              </Typography>
            );
          return (
            <FormField control={control} name={`residenceCardInformation.${row.index}.residenceCode`}>
              <Input disabled={readonly} transformToHalfWidth />
            </FormField>
          );
        },
      },
      {
        header: "在留資格",
        Cell: ({ row }) => {
          const residenceStatusIdWatchField = methods.watch(`residenceCardInformation.${row.index}.residenceStatusId`);
          return (
            <Typography variant="body14" component="p" padding="0px !important" mt={0.75}>
              {findLabelByValue(residenceStatusOptions, residenceStatusIdWatchField)}
            </Typography>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset", backgroundColor: divider.low },
        },
      },
      {
        header: "在留期間",
        Cell: ({ row }) => {
          const residenceStatusIdWatchField = methods.watch(`residenceCardInformation.${row.index}.residenceStatusId`);
          const residencePeriodIdWatchField = methods.watch(`residenceCardInformation.${row.index}.residencePeriodId`);
          if (isHistoryPage)
            return (
              <Typography variant="body14" padding="0px !important">
                {findLabelByValue(getResidencePeriodOptions(residenceStatusIdWatchField), residencePeriodIdWatchField)}
              </Typography>
            );
          return (
            <FormField control={control} name={`residenceCardInformation.${row.index}.residencePeriodId`}>
              <Select options={getResidencePeriodOptions(residenceStatusIdWatchField)} loading={isLoadingResidenceStatus} disabled={readonly} />
            </FormField>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset", backgroundColor: divider.low },
        },
      },
      {
        header: "在留期限（在留期間満了日）",
        Cell: ({ row }) => {
          if (isHistoryPage)
            return (
              <Typography variant="body14" padding="0px !important">
                {dayjs(row.original.renewVisaDocumentStep.renewVisaResidenceCardInformation?.residenceExpiryDate).format(DATE_FORMAT)}
              </Typography>
            );
          return (
            <FormField control={control} name={`residenceCardInformation.${row.index}.residenceExpiryDate`}>
              <InputDatePicker disabled={readonly} />
            </FormField>
          );
        },
      },
      {
        header: "最終更新日",
        Cell: ({ row }) => {
          return (
            <Typography variant="body14" padding="0px !important">
              {row.original.renewVisaDocumentStep.renewVisaResidenceCardInformation?.updatedAt
                ? dayjs(row.original.renewVisaDocumentStep.renewVisaResidenceCardInformation?.updatedAt).format(DATE_TIME_FORMAT)
                : EMPTY_STR.DATE_TIME}
            </Typography>
          );
        },
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset", backgroundColor: divider.low },
        },
      },
      {
        header: "カードの写し（表）",
        Cell: ({ row }) => {
          const residenceExpiryDateWatchField = methods.watch(`residenceCardInformation.${row.index}.residenceExpiryDate`);

          const employeeFirstName = row.original.employee?.firstName ?? null;
          const residenceExpiryDate = residenceExpiryDateWatchField ? dayjs(residenceExpiryDateWatchField).format("YYYYMMDD") : null;
          const existingFiles = row.original?.employee?.residenceCardFrontFilePath || [];

          const handleCheckExistFileName = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
            if (!residenceExpiryDate) {
              methods.setError(`residenceCardInformation.${row.index}.residenceCardFrontFile`, {
                message: "データをアップロードする前に「在留期限（在留期間満了日）」を選択してください。",
              });
              e.preventDefault();
              return;
            } else {
              methods.clearErrors(`residenceCardInformation.${row.index}.residenceCardFrontFile`);
            }
          };

          return (
            <FormField control={control} name={`residenceCardInformation.${row.index}.residenceCardFrontFile`}>
              <UploadFileBtn
                fieldLabel="カードの写し（表）"
                handleCheckExistFileName={handleCheckExistFileName}
                readonly={readonly}
                employeeFirstName={employeeFirstName}
                residenceExpiryDate={residenceExpiryDate}
                existingFiles={existingFiles}
              />
            </FormField>
          );
        },
      },
      {
        header: "カードの写し（裏）",
        Cell: ({ row }) => {
          const residenceExpiryDateWatchField = methods.watch(`residenceCardInformation.${row.index}.residenceExpiryDate`);

          const employeeFirstName = row.original.employee?.firstName ?? null;
          const residenceExpiryDate = residenceExpiryDateWatchField ? dayjs(residenceExpiryDateWatchField).format("YYYYMMDD") : null;
          const existingFiles = row.original?.employee?.residenceCardBackFilePath || [];

          const handleCheckExistFileName = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
            if (!residenceExpiryDate) {
              methods.setError(`residenceCardInformation.${row.index}.residenceCardBackFile`, {
                message: "データをアップロードする前に「在留期限（在留期間満了日）」を選択してください。",
              });
              e.preventDefault();
              return;
            } else {
              methods.clearErrors(`residenceCardInformation.${row.index}.residenceCardBackFile`);
            }
          };

          return (
            <FormField control={control} name={`residenceCardInformation.${row.index}.residenceCardBackFile`}>
              <UploadFileBtn
                fieldLabel="カードの写し（裏）"
                handleCheckExistFileName={handleCheckExistFileName}
                readonly={readonly}
                employeeFirstName={employeeFirstName}
                residenceExpiryDate={residenceExpiryDate}
                existingFiles={existingFiles}
              />
            </FormField>
          );
        },
      },
    ],
    [readonly, residenceStatusOptions, methods],
  );

  const { table } = useTable({
    columns,
    rows: residenceCardInformationGroupListData ?? [],
    muiTableHeadCellProps: { sx: { backgroundColor: divider.low } },
    muiTableBodyRowProps: { sx: { verticalAlign: "top" } },
  });

  return (
    <>
      <Form methods={methods} onSubmit={onSubmitRenewVisaResidenceCardInformation} schema={renewVisaResidenceCardInformationSchema}>
        <RenewVisaStepCard
          isCompleted={stepGroupData.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
          title={`ステップ${isExistBasicInfoData ? stepOrder : stepOrder - 1}`}
          subTitle={stepName}
          buttons={
            <Button variant="primary" size="sm" type="submit" disabled={readonly || toNumber(residenceCardInformationGroupListData?.length) === 0}>
              在留カード情報更新して完了
            </Button>
          }
        >
          <Table table={table} maxHeight="45vw" />
        </RenewVisaStepCard>
      </Form>

      <WarningExcessResidenceCardFileModal
        residenceCardInformationGroupListData={
          status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE || readonly || toNumber(residenceCardInformationGroupListData?.length) === 0
            ? []
            : residenceCardInformationGroupListData || []
        }
      />
    </>
  );
};
